/*
 * Elite Coveo Recommendations Slider
 * Loaded ONCE per page. Each slider instance registers itself by pushing a
 * config object onto `window.__eliteCoveoQueue`:
 *
 *   (window.__eliteCoveoQueue = window.__eliteCoveoQueue || []).push({
 *     sliderId, slotId, productId
 *   });
 *
 * The Coveo engine + headless commerce module are created a single time and
 * shared across every slider. Global listeners (document mouseup/mouseleave,
 * window resize) are registered once and fan out to all live sliders.
 */
(function () {
  'use strict';

  // Idempotent: if this asset is somehow loaded twice, bail out.
  if (window.EliteCoveoSlider) return;

  // ---- Shared singletons (created once, reused by every slider) ----------
  let headlessPromise;
  let enginePromise;

  const getHeadless = () =>
    (headlessPromise ||= import(
      'https://static.cloud.coveo.com/shopify/v1.9/headless/commerce.esm.js'
    ));

  const getEngine = () =>
    (enginePromise ||= window
      .getCoveoConfig()
      .then((config) => window.configureHeadless(config)));

  // ---- Shared global listeners (registered once) -------------------------
  const sliders = new Set();

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  document.addEventListener('mouseup', () =>
    sliders.forEach((s) => s.onMouseUp())
  );
  document.addEventListener('mouseleave', () =>
    sliders.forEach((s) => s.onMouseLeave())
  );
  window.addEventListener(
    'resize',
    debounce(() => sliders.forEach((s) => s.onResize()), 150)
  );

  // ---- Pure helpers (defined once, shared by all instances) --------------
  function formatPrice(product) {
    const price = parseFloat(product.ec_price) || 0;
    const promo = parseFloat(product.ec_promo_price) || 0;

    if (promo && promo < price) {
      return `<span style="color: red;">$${promo.toFixed(2)}</span><span style="text-decoration: line-through; color: #111;">$${price.toFixed(2)}</span>`;
    } else if (price === 0 && promo !== 0) {
      return `<span style="color: #111;">$${promo.toFixed(2)}</span>`;
    } else if (promo > price) {
      return `<span style="color: #111;">$${promo.toFixed(2)}</span>`;
    } else {
      return `<span style="color: #111;">$${price.toFixed(2)}</span>`;
    }
  }

  function buildCardHtml(product) {
    const productUrl =
      product.clickUri ||
      `/products/${product.ec_product_id || product.permanentid}`;
    const productId = Number(product.ec_item_group_id);
    const oxyshred_ids = [9088477195,2046477336664,2056013348952];
    let saleLabel = '';
    if (
      product.ec_promo_price &&
      product.ec_price &&
      product.ec_promo_price < product.ec_price
    ) {
      saleLabel =
        '<div class="sale-badge-container"><span class="sale-ribbon">SALE</span></div>';
    }
    if(product.ec_brand == "Lunar Performance" && product.ec_promo_price && product.ec_price && product.ec_promo_price < product.ec_price){
      var $up_to_discount = parseInt(product.additionalFields.up_to_discount) || 0;
      if($up_to_discount && $up_to_discount > 0){
        saleLabel = `<div class="custom-sale-label">DEAL - UP TO ${$up_to_discount}% OFF</div>`;
      }else{
        const discount = Math.round(((product.ec_price - product.ec_promo_price) / product.ec_price) * 100);
        if(discount > 0){
          saleLabel = `<div class="custom-sale-label">DEAL - ${discount}% OFF</div>`;
        }
      }
    }

    let optionsHtml = '';
    let options = [];
    try {
      const parsed = JSON.parse(product?.additionalFields?.option_obj || '{}');
      options = Array.isArray(parsed?.options) ? parsed.options : [];
    } catch (e) {}
    for (let i = 0; i < options.length; i++) {
      optionsHtml += `<span>${options[i].value} ${options[i].name}</span>`;
    }

    return `
      <div class="product-card-container">
        ${saleLabel}
        <a href="${productUrl}" class="product-link" data-product-id="${product.permanentid}" style="text-decoration: none;">

          <div class="product-image-wrapper">
            ${
              product.ec_thumbnails && product.ec_thumbnails[0]
                ? `
              <img
                src="${product.ec_thumbnails[0]}&width=300"
                alt="${product.ec_name || 'Product'}"
                width="150"
                height="150"
                loading="lazy"
              />
            `
                : '<div style="width: 100%; height: 100%; background: #f5f5f5;"></div>'
            }
          </div>
          <div class="option-wrapper">${optionsHtml}</div>
          <div class="product-title">
            <h3>${product.ec_name || 'Product'}</h3>
          </div>

        <div class="klaviyo-star-rating-widget" data-id="${productId}" data-product-title="${(product.ec_name || '').substring(0, 25)}" data-product-type="configurable"></div>

          <div class="product-price-wrapper">
            <div class="product-price" style="font-family: var(--branding-font, Arial, sans-serif); font-size: 18px; font-weight: 600; display: flex; align-items: baseline; justify-content: center; gap: 4px; white-space: nowrap;">
              ${formatPrice(product)}
            </div>
          </div>
        </a>

        <div class="quick-wrapper">
        <modal-opener data-modal="#QuickAdd-${productId}" style="display: flex; justify-content: center;">
          <button id="${productId}-submit" type="submit" name="add" class="button quick-buy-btn" aria-haspopup="dialog" aria-labelledby="${productId}-submit" data-product-url="${productUrl}" style="background-color: #1a1a1a; color: white; border: none; border-radius: 4px; cursor: pointer; font-family: var(--branding-font); margin: 5px 0 0 0;">
            Quick Buy
            <div class="loading__spinner hidden">
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" width="20px" height="20px">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </button>
        </modal-opener>

        <quick-add-modal id="QuickAdd-${productId}" class="quick-add-modal">
          <div role="dialog" aria-label="${product.ec_name || 'Product'}" aria-modal="true" class="quick-add-modal__content global-settings-popup" tabindex="-1">
            <button id="ModalClose-${productId}" type="button" class="quick-add-modal__toggle" aria-label="Close">
              <svg class="icon icon-close" fill="#000000" xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 1.3 1.3" enable-background="new 0 0 52 52" xml:space="preserve"><path d="m0.775 0.635 0.325 -0.328c0.015 -0.015 0.015 -0.038 0 -0.053l-0.05 -0.053c-0.015 -0.015 -0.038 -0.015 -0.053 0L0.67 0.53c-0.01 0.01 -0.025 0.01 -0.035 0L0.308 0.2c-0.015 -0.015 -0.038 -0.015 -0.053 0l-0.053 0.053c-0.015 0.015 -0.015 0.038 0 0.053l0.328 0.328c0.01 0.01 0.01 0.025 0 0.035L0.2 0.998c-0.015 0.015 -0.015 0.038 0 0.053l0.053 0.053c0.015 0.015 0.038 0.015 0.053 0L0.633 0.775c0.01 -0.01 0.025 -0.01 0.035 0l0.328 0.328c0.015 0.015 0.038 0.015 0.053 0L1.1 1.05c0.015 -0.015 0.015 -0.038 0 -0.053L0.775 0.67c-0.01 -0.01 -0.01 -0.025 0 -0.035"></path></svg>
            </button>
            <div id="QuickAddInfo-${productId}" class="quick-add-modal__content-info"></div>
          </div>
        </quick-add-modal>
        </div>
      </div>
    `;
  }

  // ---- Per-instance setup ------------------------------------------------
  async function init(cfg) {
    const container = document.getElementById(cfg.sliderId);
    if (!container || container.dataset.eliteInit) return; // guard double-init
    container.dataset.eliteInit = '1';

    const [headlessCommerce, engine] = await Promise.all([
      getHeadless(),
      getEngine(),
    ]);

    const options = { slotId: cfg.slotId };
    if (cfg.productId && cfg.productId.trim() !== '') {
      options.productId = 'gid://shopify/ProductVariant/' + cfg.productId;
    }

    const controller = headlessCommerce.buildRecommendations(engine, {
      options,
    });

    // DOM elements
    const headline = container.querySelector('.slider-headline');
    const sliderTrack = container.querySelector('.slider-track');
    const productList = container.querySelector('.slider-product-list');
    const prevBtn = container.querySelector('.slider-nav-prev');
    const nextBtn = container.querySelector('.slider-nav-next');

    // Slider state
    let itemsPerView = 5;
    let totalProducts = 0;
    let itemWidth = 0;
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasDragged = false;

    function updateItemsPerView() {
      itemsPerView = window.innerWidth <= 768 ? 2 : 5;
    }

    function updateNavigation() {
      const left = sliderTrack.scrollLeft;
      const maxScroll = sliderTrack.scrollWidth - sliderTrack.clientWidth;

      if (left <= 10) prevBtn.setAttribute('disabled', 'disabled');
      else prevBtn.removeAttribute('disabled');

      if (left >= maxScroll - 10) nextBtn.setAttribute('disabled', 'disabled');
      else nextBtn.removeAttribute('disabled');
    }

    function calculateItemWidth() {
      const items = productList.querySelectorAll('.slider-product-item');
      if (items.length >= 2) {
        itemWidth = items[1].offsetLeft - items[0].offsetLeft;
      } else if (items.length === 1) {
        itemWidth = items[0].offsetWidth + 5; // width + gap
      }
    }

    function scrollSlider(direction) {
      calculateItemWidth();
      const scrollAmount = itemWidth * itemsPerView;
      const current = sliderTrack.scrollLeft;
      const target =
        direction === 'next' ? current + scrollAmount : current - scrollAmount;
      sliderTrack.scrollTo({ left: target, behavior: 'smooth' });
    }

    function renderProduct(product) {
      const li = document.createElement('li');
      li.className = 'slider-product-item';
      li.innerHTML = buildCardHtml(product);

      const link = li.querySelector('.product-link');
      link.addEventListener('click', () => {
        headlessCommerce
          .buildInteractiveProduct(engine, { options: { product } })
          .select();
      });

      return li;
    }

    function renderProducts(products) {
      productList.innerHTML = '';

      if (!products || products.length === 0) {
        container.style.display = 'none';
        return;
      }

      container.style.display = '';
      totalProducts = products.length;

      const frag = document.createDocumentFragment();
      products.forEach((product) => frag.appendChild(renderProduct(product)));
      productList.appendChild(frag);

      if (totalProducts <= itemsPerView) {
        productList.setAttribute('data-center', 'true');
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
      } else {
        productList.removeAttribute('data-center');
        prevBtn.style.display = 'flex';
        nextBtn.style.display = 'flex';
      }

      setTimeout(updateNavigation, 100);
      setTimeout(() => {
        if (window.klReviewsObject) self.klReviewsObject.renderWidgets();
      }, 1000);
    }

    // Navigation handlers (local to this slider)
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      scrollSlider('prev');
    });
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      scrollSlider('next');
    });

    // Mouse drag (desktop)
    sliderTrack.addEventListener('mousedown', (e) => {
      if (totalProducts <= itemsPerView) return;
      isDown = true;
      hasDragged = false;
      sliderTrack.style.cursor = 'grabbing';
      productList.style.cursor = 'grabbing';
      startX = e.pageX;
      scrollLeft = sliderTrack.scrollLeft;
      e.preventDefault();
    });

    sliderTrack.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const walk = (e.pageX - startX) * 1.5;
      if (Math.abs(walk) > 3) hasDragged = true;
      sliderTrack.scrollLeft = scrollLeft - walk;
    });

    // Block link clicks that were actually drags
    const blockIfDragged = (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    sliderTrack.addEventListener('click', blockIfDragged, true);
    productList.addEventListener('click', blockIfDragged, true);

    // Update nav on scroll (debounced, passive)
    sliderTrack.addEventListener('scroll', debounce(updateNavigation, 50), {
      passive: true,
    });

    // Register hooks for the shared global listeners
    sliders.add({
      onMouseUp() {
        isDown = false;
        sliderTrack.style.cursor = 'grab';
        productList.style.cursor = 'grab';
        setTimeout(() => {
          hasDragged = false;
        }, 10);
      },
      onMouseLeave() {
        isDown = false;
        sliderTrack.style.cursor = 'grab';
        productList.style.cursor = 'grab';
      },
      onResize() {
        updateItemsPerView();
        updateNavigation();
      },
    });

    // Subscribe to Coveo state changes
    controller.subscribe(() => {
      const state = controller.state;
      headline.textContent = state.headline || 'Recommended for You';
      renderProducts(state.products || []);
    });

    // Initial load
    updateItemsPerView();
    const state = controller.state;
    if (!state.isLoading && !state.responseId) {
      controller.refresh();
    } else {
      headline.textContent = state.headline || 'Recommended for You';
      renderProducts(state.products || []);
    }
  }

  // ---- Drain anything queued before this asset loaded, then take over ----
  const queued = Array.isArray(window.__eliteCoveoQueue)
    ? window.__eliteCoveoQueue
    : [];
  window.EliteCoveoSlider = { init };
  window.__eliteCoveoQueue = { push: init };
  queued.forEach(init);
})();
